import { Dispatch, RefObject, useRef } from "react";

/**
 * Interface defining the properties of a focusable element reference
 */
interface Focusable {
  current: { focus: () => void };
}

/**
 * Helper to determine if the passed reference is focusable
 * @param {any} ref The reference passed to the hook
 */
const canFocus = (ref: any): ref is Focusable => Boolean(ref && ref.current && ref.current.focus);

/**
 * A hook with a persisting reference and a function to give focus to the reference
 * @template {Type} Type The type of the element reference
 * @param {Type} inputRef The initial element reference
 * @returns {[Type, Dispatch<void>]}
 * The element reference to be focused and the function to set the focus
 */
const useFocus = <Type>(inputRef?: Type): [RefObject<Type>, Dispatch<void>] => {
  const htmlElRef = useRef<Type>(inputRef ?? null);
  const setFocus: Dispatch<void> = () => {
    canFocus(htmlElRef) && htmlElRef.current.focus();
  };

  return [htmlElRef, setFocus];
};

export default useFocus;
